import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import {
  X, Mail, Paperclip, Users, UserPlus, Trash2,
  Send, Eye, ChevronDown, ChevronUp, Plus
} from 'lucide-react';

const BoardReportEmailModal = ({ isOpen, onClose, orgName }) => {
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [manualEmails, setManualEmails] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [boardMembers, setBoardMembers] = useState([]);
  const [showAddBoard, setShowAddBoard] = useState(false);
  const [newBoardName, setNewBoardName] = useState('');
  const [newBoardEmail, setNewBoardEmail] = useState('');
  const [newBoardRole, setNewBoardRole] = useState('Board Member');
  const [expandedSection, setExpandedSection] = useState('preview');
  const [selectedRecipients, setSelectedRecipients] = useState(new Set());

  useEffect(() => {
    if (isOpen) {
      fetchEmailPreview();
      fetchBoardMembers();
    }
  }, [isOpen]);

  const fetchEmailPreview = async () => {
    try {
      setLoading(true);
      const response = await api.get('/hoa/board-report/email-preview');
      setPreview(response.data);

      // Auto-select all recipients
      const allEmails = new Set();
      (response.data.recipients?.owners || []).forEach(r => { if (r.email) allEmails.add(r.email); });
      (response.data.recipients?.board_members || []).forEach(r => { if (r.email) allEmails.add(r.email); });
      setSelectedRecipients(allEmails);
    } catch (error) {
      console.error('Error fetching email preview:', error);
      toast.error('Failed to load email preview');
    } finally {
      setLoading(false);
    }
  };

  const fetchBoardMembers = async () => {
    try {
      const response = await api.get('/hoa/board-members');
      setBoardMembers(response.data.board_members || []);
    } catch (error) {
      console.error('Error fetching board members:', error);
    }
  };

  const handleAddBoardMember = async () => {
    if (!newBoardEmail.trim()) {
      toast.error('Email is required');
      return;
    }
    try {
      const response = await api.post('/hoa/board-members', {
        name: newBoardName.trim(),
        email: newBoardEmail.trim(),
        role: newBoardRole.trim() || 'Board Member'
      });
      setBoardMembers(prev => [...prev, response.data.board_member]);
      setSelectedRecipients(prev => new Set([...prev, newBoardEmail.trim()]));
      setNewBoardName('');
      setNewBoardEmail('');
      setNewBoardRole('Board Member');
      setShowAddBoard(false);
      toast.success('Board member added');
    } catch (error) {
      toast.error('Failed to add board member');
    }
  };

  const handleDeleteBoardMember = async (memberId, email) => {
    try {
      await api.delete(`/hoa/board-members/${memberId}`);
      setBoardMembers(prev => prev.filter(m => m.id !== memberId));
      setSelectedRecipients(prev => {
        const next = new Set(prev);
        next.delete(email);
        return next;
      });
      toast.success('Board member removed');
    } catch (error) {
      toast.error('Failed to remove board member');
    }
  };

  const handleAddManualEmail = () => {
    if (!newEmail.trim()) return;
    const email = newEmail.trim();
    if (manualEmails.find(m => m.email === email)) {
      toast.error('Email already added');
      return;
    }
    setManualEmails(prev => [...prev, { name: newName.trim() || email.split('@')[0], email }]);
    setSelectedRecipients(prev => new Set([...prev, email]));
    setNewEmail('');
    setNewName('');
  };

  const handleRemoveManualEmail = (email) => {
    setManualEmails(prev => prev.filter(m => m.email !== email));
    setSelectedRecipients(prev => {
      const next = new Set(prev);
      next.delete(email);
      return next;
    });
  };

  const toggleRecipient = (email) => {
    setSelectedRecipients(prev => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  };

  const totalSelected = selectedRecipients.size;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="board-email-modal-overlay">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="board-email-modal">
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-700 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Mail className="h-5 w-5 text-blue-300" />
            <div>
              <h2 className="text-white font-semibold text-lg">Send Board Report</h2>
              <p className="text-slate-300 text-xs">Preview and manage recipients</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-300 hover:text-white transition" data-testid="board-email-modal-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-700"></div>
          </div>
        ) : (
          <div className="overflow-y-auto flex-1 p-6 space-y-6">
            {/* Email Preview Section */}
            <div>
              <button
                onClick={() => setExpandedSection(expandedSection === 'preview' ? '' : 'preview')}
                className="flex items-center justify-between w-full text-left"
                data-testid="toggle-email-preview"
              >
                <div className="flex items-center gap-2">
                  <Eye className="h-4 w-4 text-slate-600" />
                  <span className="font-semibold text-slate-800">Email Preview</span>
                </div>
                {expandedSection === 'preview' ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
              </button>

              {expandedSection === 'preview' && preview && (
                <div className="mt-3 space-y-3">
                  {/* Subject */}
                  <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                    <p className="text-xs text-slate-500 mb-1">Subject</p>
                    <p className="font-medium text-slate-800 text-sm" data-testid="email-subject">{preview.subject}</p>
                  </div>

                  {/* Attachment indicator */}
                  <div className="flex items-center gap-2 bg-green-50 rounded-lg p-3 border border-green-200">
                    <Paperclip className="h-4 w-4 text-green-600" />
                    <div>
                      <p className="text-sm font-medium text-green-800" data-testid="email-attachment">{preview.attachment_name}</p>
                      <p className="text-xs text-green-600">PDF Attachment</p>
                    </div>
                  </div>

                  {/* Body HTML preview */}
                  <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <div className="bg-slate-100 px-3 py-2 border-b border-slate-200">
                      <p className="text-xs text-slate-500">Email Body</p>
                    </div>
                    <div
                      className="p-4 bg-white"
                      data-testid="email-body-preview"
                      dangerouslySetInnerHTML={{ __html: preview.body_html }}
                    />
                  </div>
                </div>
              )}
            </div>

            <hr className="border-slate-200" />

            {/* Recipients Section */}
            <div>
              <button
                onClick={() => setExpandedSection(expandedSection === 'recipients' ? '' : 'recipients')}
                className="flex items-center justify-between w-full text-left"
                data-testid="toggle-recipients"
              >
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-slate-600" />
                  <span className="font-semibold text-slate-800">Recipients</span>
                  <Badge variant="secondary" className="text-xs">{totalSelected} selected</Badge>
                </div>
                {expandedSection === 'recipients' ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
              </button>

              {expandedSection === 'recipients' && (
                <div className="mt-3 space-y-4">
                  {/* Board Members */}
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
                          <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                          Board Members
                        </h4>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setShowAddBoard(!showAddBoard)}
                          className="text-xs h-7"
                          data-testid="add-board-member-toggle"
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          Add
                        </Button>
                      </div>

                      {showAddBoard && (
                        <div className="bg-blue-50 rounded-lg p-3 mb-3 space-y-2 border border-blue-100">
                          <div className="grid grid-cols-3 gap-2">
                            <div>
                              <Label className="text-xs">Name</Label>
                              <Input
                                value={newBoardName}
                                onChange={e => setNewBoardName(e.target.value)}
                                placeholder="John Doe"
                                className="h-8 text-sm"
                                data-testid="board-member-name-input"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Email</Label>
                              <Input
                                type="email"
                                value={newBoardEmail}
                                onChange={e => setNewBoardEmail(e.target.value)}
                                placeholder="john@example.com"
                                className="h-8 text-sm"
                                data-testid="board-member-email-input"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Role</Label>
                              <Input
                                value={newBoardRole}
                                onChange={e => setNewBoardRole(e.target.value)}
                                placeholder="Board Member"
                                className="h-8 text-sm"
                              />
                            </div>
                          </div>
                          <Button size="sm" onClick={handleAddBoardMember} className="h-7 text-xs" data-testid="save-board-member-btn">
                            <UserPlus className="h-3 w-3 mr-1" /> Save Board Member
                          </Button>
                        </div>
                      )}

                      {boardMembers.length === 0 && !showAddBoard ? (
                        <p className="text-xs text-slate-400 italic">No board members added yet. Click "Add" to create your board list.</p>
                      ) : (
                        <div className="space-y-1.5">
                          {boardMembers.map(member => (
                            <div key={member.id} className="flex items-center justify-between bg-white rounded px-2 py-1.5 border border-slate-100 hover:border-slate-200 transition">
                              <label className="flex items-center gap-2 cursor-pointer flex-1">
                                <input
                                  type="checkbox"
                                  checked={selectedRecipients.has(member.email)}
                                  onChange={() => toggleRecipient(member.email)}
                                  className="rounded border-slate-300"
                                />
                                <div>
                                  <p className="text-sm font-medium text-slate-700">{member.name || member.email}</p>
                                  <p className="text-xs text-slate-400">{member.email} - {member.role}</p>
                                </div>
                              </label>
                              <button
                                onClick={() => handleDeleteBoardMember(member.id, member.email)}
                                className="text-slate-300 hover:text-red-500 transition p-1"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Homeowners */}
                  <Card>
                    <CardContent className="p-4">
                      <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5 mb-3">
                        <span className="w-2 h-2 rounded-full bg-green-500"></span>
                        Homeowners ({(preview?.recipients?.owners || []).length})
                      </h4>
                      <div className="max-h-40 overflow-y-auto space-y-1.5" data-testid="owner-recipients-list">
                        {(preview?.recipients?.owners || []).length === 0 ? (
                          <p className="text-xs text-slate-400 italic">No homeowners with email addresses found.</p>
                        ) : (
                          (preview.recipients.owners).map((owner, idx) => (
                            <label key={idx} className="flex items-center gap-2 cursor-pointer px-2 py-1.5 rounded hover:bg-slate-50 transition">
                              <input
                                type="checkbox"
                                checked={selectedRecipients.has(owner.email)}
                                onChange={() => toggleRecipient(owner.email)}
                                className="rounded border-slate-300"
                              />
                              <div>
                                <p className="text-sm text-slate-700">{owner.name || owner.email}</p>
                                <p className="text-xs text-slate-400">{owner.email}{owner.unit ? ` - Unit ${owner.unit}` : ''}</p>
                              </div>
                            </label>
                          ))
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Manual Entry */}
                  <Card>
                    <CardContent className="p-4">
                      <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5 mb-3">
                        <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                        Additional Recipients
                      </h4>
                      <div className="flex gap-2 mb-3">
                        <Input
                          value={newName}
                          onChange={e => setNewName(e.target.value)}
                          placeholder="Name (optional)"
                          className="h-8 text-sm flex-1"
                          data-testid="manual-recipient-name"
                        />
                        <Input
                          type="email"
                          value={newEmail}
                          onChange={e => setNewEmail(e.target.value)}
                          placeholder="email@example.com"
                          className="h-8 text-sm flex-1"
                          data-testid="manual-recipient-email"
                          onKeyDown={e => e.key === 'Enter' && handleAddManualEmail()}
                        />
                        <Button size="sm" onClick={handleAddManualEmail} className="h-8 text-xs" data-testid="add-manual-recipient-btn">
                          <Plus className="h-3 w-3" />
                        </Button>
                      </div>
                      {manualEmails.length > 0 && (
                        <div className="space-y-1.5">
                          {manualEmails.map((m, idx) => (
                            <div key={idx} className="flex items-center justify-between bg-orange-50 rounded px-2 py-1.5 border border-orange-100">
                              <label className="flex items-center gap-2 cursor-pointer flex-1">
                                <input
                                  type="checkbox"
                                  checked={selectedRecipients.has(m.email)}
                                  onChange={() => toggleRecipient(m.email)}
                                  className="rounded border-slate-300"
                                />
                                <div>
                                  <p className="text-sm text-slate-700">{m.name}</p>
                                  <p className="text-xs text-slate-400">{m.email}</p>
                                </div>
                              </label>
                              <button onClick={() => handleRemoveManualEmail(m.email)} className="text-slate-300 hover:text-red-500 transition p-1">
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="border-t border-slate-200 px-6 py-4 bg-slate-50 flex items-center justify-between">
          <p className="text-xs text-slate-500">
            {totalSelected} recipient{totalSelected !== 1 ? 's' : ''} selected
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose} data-testid="email-modal-cancel-btn">
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={totalSelected === 0}
              className="bg-slate-700 hover:bg-slate-800 text-white"
              onClick={() => toast.info('Email sending will be available soon. SendGrid integration coming next!')}
              data-testid="send-board-report-btn"
            >
              <Send className="h-3.5 w-3.5 mr-1.5" />
              Send to {totalSelected} Recipient{totalSelected !== 1 ? 's' : ''}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BoardReportEmailModal;
